import { Modes } from '@constants/canvas/layers';
import { Tabs } from '@views/Insights/types';

export type FilterAvailability = 'available' | 'hidden' | 'locked';

export type FilterAvailabilityInput = {
  mode: Modes | null;
  isDashboardMode: boolean;
  isInsightsMode: boolean;
  insightsTab: Tabs;
  isVersionsShown: boolean;
};

const WHOLE_MODEL_INSIGHT_TABS = new Set<Tabs>([Tabs.KeyControls]);

export const ignoresModelFilters = ({
  isInsightsMode,
  insightsTab,
}: Pick<FilterAvailabilityInput, 'isInsightsMode' | 'insightsTab'>) =>
  isInsightsMode && WHOLE_MODEL_INSIGHT_TABS.has(insightsTab);

/**
 * Which of its three states the toolbar's filter funnel is in for a mode and
 * view: `available` renders the working funnel, `hidden` renders neither the
 * funnel nor its separator, and `locked` renders it dimmed and inert with the
 * selection still applied.
 *
 * Cyber resilience offers no model filtering on any view, so the funnel is
 * hidden there; the Risk register tab exists only in that mode and needs no
 * rule of its own. Before the mode is known there is nothing to filter, and
 * the version history freezes the whole toolbar, so both read as hidden too.
 * The Dashboard offers only the Controls mode's partner filter. Inside an
 * insight breakdown the funnel is locked; the Insights main view is the
 * `default` tab and stays available. A breakdown the filter does not apply to
 * at all (`ignoresModelFilters`) hides the funnel instead, since a locked one
 * would claim the selection still shapes the results.
 */
export const resolveFilterAvailability = ({
  mode,
  isDashboardMode,
  isInsightsMode,
  insightsTab,
  isVersionsShown,
}: FilterAvailabilityInput): FilterAvailability => {
  if (mode === null || mode === Modes.RiskManagement || isVersionsShown) {
    return 'hidden';
  }

  if (isDashboardMode) {
    return mode === Modes.Controls ? 'available' : 'hidden';
  }

  if (ignoresModelFilters({ isInsightsMode, insightsTab })) {
    return 'hidden';
  }

  if (isInsightsMode && insightsTab !== Tabs.Default) {
    return 'locked';
  }

  return 'available';
};
